'use client';

import { useState } from 'react';
import { ChevronDown, Expand01, HelpCircle, X } from '@untitledui/icons';
import { Dialog, Modal, ModalOverlay } from '@untitledui/react/components/application/modals/modal';
import type { FieldHelp, HelpMedia } from '@caverne/shared';

interface HelpDisclosureProps {
  help: FieldHelp;
  /**
   * Dossier des assets publics, déjà résolu par l'application (`publicAsset('/assets')`).
   * Sans lui, l'aide se limite à son texte.
   */
  mediaBase?: string;
}

/**
 * Les chemins viennent du catalogue et peuvent contenir des espaces ou un sous-dossier :
 * on encode segment par segment, sans écraser les `/`.
 */
function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/**
 * Aide « Comment trouver ? » d'un champ technique.
 *
 * L'aide peut porter des illustrations animées, déclinées par système (Windows, Android,
 * iOS) : chacune a sa propre légende et ses propres étapes. Les vignettes sont réduites
 * pour ne pas noyer le formulaire — un clic les agrandit. Un fichier absent retombe sur le
 * cadre d'attente, pour qu'une image manquante ne casse jamais le formulaire.
 */
export function HelpDisclosure({ help, mediaBase }: HelpDisclosureProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  const [zoomed, setZoomed] = useState<HelpMedia | null>(null);

  const media = (help.media ?? []).filter((item) => !failed.includes(item.file));
  const hasMedia = Boolean(mediaBase) && media.length > 0;
  /** Deux systèmes (Android / iPhone) : affichage en colonnes, rangées alignées. */
  const isMulti = media.length > 1;
  const srcOf = (item: HelpMedia) => `${mediaBase}/${encodePath(item.file)}`;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="text-tertiary hover:text-secondary flex items-center gap-1 text-xs font-medium transition-colors"
      >
        <HelpCircle className="size-3.5" />
        Comment trouver&nbsp;?
        <ChevronDown className={`size-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="border-secondary bg-secondary mt-2 rounded-lg border p-3">
          <p className="text-secondary text-xs font-medium">{help.caption}</p>

          {help.steps.length > 0 && (
            <ol className="text-tertiary mt-2 list-decimal space-y-1 pl-4 text-xs">
              {help.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}

          {hasMedia && (
            <div className={`mt-3 grid gap-3 ${isMulti ? 'sm:grid-cols-2' : ''}`}>
              {media.map((item, index) => (
                <figure
                  key={item.file}
                  /* Deux systèmes côte à côte : `subgrid` fait partager les mêmes rangées aux
                     colonnes, pour que les images s'alignent même si un texte tient sur deux
                     lignes et l'autre sur une. Les trois emplacements sont donc toujours rendus.
                     Un filet les sépare : vertical en colonnes, horizontal une fois empilés. */
                  className={[
                    'm-0',
                    isMulti ? 'sm:row-span-3 sm:grid sm:grid-rows-subgrid' : '',
                    isMulti && index > 0
                      ? 'border-secondary border-t pt-3 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-3'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <figcaption className="text-secondary text-xs font-medium">
                    {item.caption}
                  </figcaption>
                  <ul className="text-tertiary mt-1 space-y-1 text-xs sm:mt-0">
                    {(item.steps ?? []).map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ul>

                  {/* Vignette cliquable : le GIF s'anime déjà, le clic l'agrandit. */}
                  <button
                    type="button"
                    onClick={() => setZoomed(item)}
                    aria-label={`Agrandir l’illustration : ${item.alt}`}
                    className={`group border-secondary bg-primary outline-focus-ring relative mx-auto mt-2 block w-full cursor-zoom-in overflow-hidden rounded-md border focus-visible:outline-2 focus-visible:outline-offset-2 ${isMulti ? 'sm:mt-0' : ''}`}
                  >
                    {/* `<img>` natif : l'URL est déjà résolue par l'application, et le GIF doit
                        s'animer — `next/image` n'apporterait rien ici. */}
                    <img
                      src={srcOf(item)}
                      alt={item.alt}
                      loading="lazy"
                      decoding="async"
                      onError={() => setFailed((current) => [...current, item.file])}
                      className="mx-auto max-h-72 w-auto max-w-full object-contain"
                    />
                    <span className="pointer-events-none absolute right-1.5 bottom-1.5 flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                      <Expand01 className="size-3" />
                      Agrandir
                    </span>
                  </button>
                </figure>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Agrandissement — `Échap` et le clic hors cadre referment (react-aria). */}
      <ModalOverlay
        isOpen={zoomed !== null}
        onOpenChange={(open) => !open && setZoomed(null)}
        isDismissable
      >
        <Modal className="w-full max-w-4xl">
          <Dialog>
            {zoomed && (
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-primary text-sm font-semibold">
                      {zoomed.caption ?? help.caption}
                    </p>
                    {zoomed.steps && zoomed.steps.length > 0 && (
                      <p className="text-tertiary mt-0.5 text-xs">{zoomed.steps.join(' ')}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setZoomed(null)}
                    aria-label="Fermer l’agrandissement"
                    className="text-tertiary hover:bg-secondary hover:text-secondary shrink-0 rounded-md p-1.5 transition-colors"
                  >
                    <X className="size-5" />
                  </button>
                </div>
                <img
                  src={srcOf(zoomed)}
                  alt={zoomed.alt}
                  className="mx-auto mt-3 max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
                />
              </div>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}
